import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

/**
 * NQ Metric — a large Montserrat, tabular-nums figure used across portfolio /
 * comparison surfaces. Sizes mirror the current app's exaggerated stat sizes.
 * Shared primitive: reuse for any KPI / big number instead of ad-hoc spans.
 */
const metricVariants = cva(
  'font-display tabular-nums leading-none tracking-[-0.02em]',
  {
    variants: {
      size: {
        sm: 'text-base font-bold',
        md: 'text-xl font-bold',
        lg: 'text-[1.65rem] font-extrabold',
        xl: 'text-[2rem] font-bold',
        hero: 'text-[2.85rem] font-extrabold',
      },
      tone: {
        default: 'text-foreground',
        up: 'text-[color:var(--nq-up-bright)]',
        down: 'text-[color:var(--nq-down-soft)]',
        accent: 'text-primary',
        gold: 'text-secondary',
      },
      glow: { true: '', false: '' },
    },
    compoundVariants: [
      { tone: 'up', glow: true, class: '[text-shadow:var(--glow-up-text)]' },
    ],
    defaultVariants: { size: 'xl', tone: 'default', glow: false },
  }
);

export interface MetricProps
  extends React.HTMLAttributes<HTMLSpanElement>,
    VariantProps<typeof metricVariants> {}

export function Metric({ className, size, tone, glow, ...props }: MetricProps) {
  return <span className={cn(metricVariants({ size, tone, glow }), className)} {...props} />;
}
